import * as angular from "angular";
import * as _ from "lodash";
import {PoliciesAlertsService} from "./policies-alerts.service";
import {PoliciesRequestsService} from "./policies-requests.service";
import {PoliciesService} from "./policies-service.service";
import {SiteContextService} from "../../commons/services/site-context.service";
import {BatchRequestsService} from "../../batch/services/batch-requests.service";
import {ADMIN_USERS_IMPORT_CONFIG} from "../constants/admin-users-import.const";

export class PoliciesDialogService {

  /* @ngInject */
  constructor(private $mdDialog, private $log, private uploadFile, private policiesService: PoliciesService, private siteContextService: SiteContextService,
              private policiesAlertsService: PoliciesAlertsService, private policiesRequestsService: PoliciesRequestsService, private $mdMedia,
              private IAMPermissions, private batchRequestsService: BatchRequestsService) {
  }

  // ******************************
  // External methods
  // ******************************

  /**
   * PRD-1725 - "Upload Admin Users" modal. Resolves with the validated rows
   * (ImportVerification<IamUser>[]) returned by /importers-users/validate so the caller can review
   * them before committing the import.
   */
  showUploadAdminUsersDialog(event, schemas?: any[]) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/upload-admin-users-dialog.html').default,
      targetEvent: event,
      clickOutsideToClose: false,
      fullscreen: true,
      controller: function ($scope) {
        $scope.loading = false;
        $scope.file = undefined;
        $scope.schemaId = undefined;
        $scope.validationError = undefined;
        $scope.filePattern = ADMIN_USERS_IMPORT_CONFIG.filePattern;
        $scope.schemaOptionsWrapper = {
          list: _.map(schemas || [], (schema) => {
            return {id: schema.id, name: schema.schemaName};
          }),
          propertyRef: 'name'
        };

        $scope.cancel = self.$mdDialog.cancel;

        $scope.onSelectSchema = (schemaId) => {
          $scope.schemaId = schemaId;
          $scope.validationError = undefined;
        };

        $scope.onAddFile = (file) => {
          $scope.file = file;
          $scope.validationError = undefined;
        };

        $scope.onUpload = () => {
          if (!$scope.file) {
            $scope.validationError = 'Please select a file to upload';
            return;
          }

          $scope.loading = true;

          self.batchRequestsService.validateAgentAssignments(ADMIN_USERS_IMPORT_CONFIG.importerPath, $scope.file, $scope.schemaId)
            .then((rows: any) => {
              self.$mdDialog.hide(rows || []);
            })
            .catch((error) => {
              $scope.validationError = 'Failed to validate the uploaded file';
              self.$log.error('Failed to validate admin users file, ' +
                'policiesDialogService@showUploadAdminUsersDialog', error);
            })
            .finally(() => {
              $scope.loading = false;
            });
        };
      }
    });
  }

  addUser(event, roleOptions?, groupOptions?, passwordPolicy?) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-new-admin-user-dialog.html').default,
      controller: function ($scope) {

        const roleOptionsCopy = angular.copy(roleOptions);
        const roles = self.policiesService.getRolesWithPermissionsByVersion(roleOptionsCopy);

        $scope.rolesMap = self.policiesService.translateRolesMap(self.policiesService.getRoleMap(roles));
        $scope.isSuperOwner = self.IAMPermissions.hasRole('super.owner');


        $scope.selectedRoleChips = [];
        $scope.selectedGroupChips = [];

        $scope.isMobile = !self.$mdMedia('gt-md');

        $scope.isSite = self.siteContextService.isSite();

        const groupOptionsCopy = angular.copy(groupOptions);
        $scope.groupOptions = groupOptionsCopy;

        $scope.roles = angular.copy(roles);

        $scope.logoStatus = 'idle';
        $scope.userRoles = [];
        $scope.userGroups = [];

        $scope.passwordPolicy = passwordPolicy;

        $scope.payload = {
          user: {
            firstName: undefined,
            lastName: undefined,
            email: undefined,
            password: undefined,
            pictureUrl: undefined,
          },
          expirationDays: undefined,
          branchIds: undefined,
          bindings: []
        };

        $scope.cancel = self.$mdDialog.cancel;
        // ******************************
        // External methods
        // ******************************

        $scope.onTogglePermanentUser = () => {
          $scope.payload.expirationDays = undefined;
        }

        $scope.onToggleTemporaryUser = () => {
          $scope.payload.expirationDays = 1;
        }

        $scope.onChangeUserExpirationDays = (amount) => {
          $scope.payload.expirationDays = amount;
        }

        $scope.onChangeTimezone = (zoneId) => {
          $scope.payload.user.zoneId = zoneId;
        }

        $scope.onChangeRoles = (updatedRoles) => {
          $scope.userRoles = updatedRoles;
          $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        }

        $scope.onRemoveRoleChip = (role) => {
          $scope.userRoles = _.filter($scope.userRoles, (r) => {
            return r.id !== role.id;
          });
          $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        }

        $scope.rolesToChip = (roles) => {
          return _.map(roles, (r) => {
            return {name: 'roles.' + r.id, id: r.id};
          });
        }

        $scope.onClearRoles = () => {
          $scope.userRoles = [];
          $scope.selectedRoleChips = [];
        }

        $scope.onChangeGroups = (updatedGroups) => {
          $scope.userGroups = updatedGroups;
          $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);
        }

        $scope.onRemoveGroupChip = (group) => {
          $scope.userGroups = _.filter($scope.userGroups, (r) => {
            return r.id !== group.id;
          });

          $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);
        }

        $scope.groupsToChip = (groups) => {
          return _.map(groups, (g) => {
            return {name: g.name, id: g.id};
          });
        }

        $scope.onClearGroups = () => {
          $scope.userGroups = [];
          $scope.selectedGroupChips = [];
        }

        $scope.onPasswordChange = (password) => {
          $scope.payload.user.password = password
        }

        $scope.onBranchesSelect = (selectedIds) => {
          $scope.payload.branchIds = selectedIds
        }

        $scope.onLogoUpload = ($files) => {
          $scope.logoStatus = 'upload';
          self.uploadFile($files[0])
            .then((response) => {
              $scope.logoStatus = 'complete';
              $scope.payload.user.pictureUrl = _.get(response,
                'data.url');
            })
            .catch((error) => {
              if (error) {
                $scope.logoStatus = 'idle';
                $scope.payload.user.pictureUrl = undefined;
                self.$log.error('Failed to upload file, policiesDialogService@onLogoUpload', error);
              }
            })
        }

        $scope.submit = (form) => {
          if (form) {
            form.$setSubmitted(true);
            if (form.$valid) {
              if ($scope.userRoles.length || $scope.userGroups.length) {
                const payloadCopy = angular.copy($scope.payload);

                const roleIdsList: any[] = _.map($scope.userRoles, (role) => {
                  return {roleId: role.id}
                });

                const groupIdsList: any[] = _.map($scope.userGroups, (group) => {
                  return {rolesGroupId: group.id}
                })

                payloadCopy.bindings = roleIdsList.concat(groupIdsList);
                self.$mdDialog.hide(payloadCopy);
              } else {
                self.policiesAlertsService.invalidRoleSelectionInfo();
              }

            }
          }
        }

      },
      targetEvent: event,
      clickOutsideToClose: true,
      fullscreen: true
    })
  }

  editUserRoles(event, user, userRoleIds, roleOptions, userGroupIds?, groupOptions?) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/edit-policy-by-user-dialog.html').default,
      controller: function ($scope) {

        const roleOptionsCopy = angular.copy(roleOptions);
        const roles = self.policiesService.getRolesWithPermissionsByVersion(roleOptionsCopy);

        $scope.rolesMap = self.policiesService.translateRolesMap(self.policiesService.getRoleMap(roles));

        $scope.groupOptions = angular.copy(groupOptions);

        $scope.roles = angular.copy(roles);
        $scope.userRoles = _.filter((roles || []), (r) => {
          return _.includes(userRoleIds, r.id);
        });
        $scope.userGroups = _.filter((groupOptions || []), (group) => {
          return _.includes(userGroupIds, group.id);
        });

        $scope.cancel = self.$mdDialog.cancel;
        // ******************************
        // External methods
        // ******************************

        $scope.rolesToChip = (roles) => {
          return _.map(roles, (r) => {
            return {name: 'roles.' + r.id, id: r.id};
          });
        }

        $scope.groupsToChip = (groups) => {
          return _.map(groups, (g) => {
            return {name: g.name, id: g.id};
          });
        }

        $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);

        $scope.onChangeRoles = (updatedRoles) => {
          $scope.userRoles = updatedRoles;
          $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        }

        $scope.onRemoveRoleChip = (role) => {
          $scope.userRoles = _.filter($scope.userRoles, (r) => {
            return r.id !== role.id;
          });
          $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        }


        $scope.onClearRoles = () => {
          $scope.userRoles = [];
          $scope.selectedRoleChips = [];
        }

        $scope.onChangeGroups = (updatedGroups) => {
          $scope.userGroups = updatedGroups;
          $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);
        }

        $scope.onRemoveGroupChip = (group) => {
          $scope.userGroups = _.filter($scope.userGroups, (r) => {
            return r.id !== group.id;
          });

          $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);
        }

        $scope.onClearGroups = () => {
          $scope.userGroups = [];
          $scope.selectedGroupChips = [];
        }


        $scope.submit = (form) => {
          if (form) {
            form.$setSubmitted(true);
            if (form.$valid) {
              if ($scope.userRoles.length || $scope.userGroups.length) {
                const payload: any = {}

                const roleIdsList: any[] = _.map($scope.userRoles, (role) => {
                  return {roleId: role.id}
                });

                const groupIdsList: any[] = _.map($scope.userGroups, (group) => {
                  return {rolesGroupId: group.id}
                })
                const bindings = roleIdsList.concat(groupIdsList);
                self.$mdDialog.hide(bindings);
              } else {
                self.policiesAlertsService.invalidRoleSelectionInfo();
              }
            }
          }
        }
      },
      targetEvent: event,
      clickOutsideToClose: true,
      fullscreen: true
    })
  }

  addServiceAccount(event, orgId, siteId, roleOptions, policyId, groupOptions?) {
    const self = this;
    return this.$mdDialog.show({
      parent: angular.element(document.body),
      template: require('./../views/dialogs/create-new-api-key-dialog.html').default,
      controller: function ($scope) {

        const roleOptionsCopy = angular.copy(roleOptions);
        const roles = self.policiesService.getRolesWithPermissionsByVersion(roleOptionsCopy);

        $scope.roles = angular.copy(roles);
        $scope.rolesMap = self.policiesService.getRoleMap(roles);
        $scope.rolesMap = self.policiesService.translateRolesMap($scope.rolesMap);
        $scope.groupOptions = angular.copy(groupOptions);

        $scope.payload = {
          displayName: undefined,
          serviceAccount: {
            displayName: undefined
          },
          bindings: []
        };
        $scope.currentView = 'form'; // form/result/loading
        $scope.createdApiKey = undefined;

        $scope.userRoles = [];

        $scope.rolesToChip = (roles) => {
          return _.map(roles, (r) => {
            return {name: 'roles.' + r.id, id: r.id};
          });
        }

        $scope.groupsToChip = (groups) => {
          return _.map(groups, (g) => {
            return {name: g.name, id: g.id};
          });
        }

        $scope.selectedRoleChips = [];
        $scope.selectedGroupChips = [];

        $scope.onChangeRoles = (updatedRoles) => {
          $scope.userRoles = updatedRoles;
          $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        }

        $scope.onRemoveRoleChip = (role) => {
          $scope.userRoles = _.filter($scope.userRoles, (r) => {
            return r.id !== role.id;
          });
          $scope.selectedRoleChips = $scope.rolesToChip($scope.userRoles);
        }


        $scope.onClearRoles = () => {
          $scope.userRoles = [];
          $scope.selectedRoleChips = [];
        }

        $scope.onChangeGroups = (updatedGroups) => {
          $scope.userGroups = updatedGroups;
          $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);
        }

        $scope.onRemoveGroupChip = (group) => {
          $scope.userGroups = _.filter($scope.userGroups, (r) => {
            return r.id !== group.id;
          });

          $scope.selectedGroupChips = $scope.groupsToChip($scope.userGroups);
        }

        $scope.onClearGroups = () => {
          $scope.userGroups = [];
          $scope.selectedGroupChips = [];
        }


        $scope.cancel = self.$mdDialog.cancel;
        // ******************************
        // External methods
        // ******************************

        $scope.submit = (form) => {
          if (form) {
            form.$setSubmitted(true);
            if (form.$valid) {
              const payloadCopy = angular.copy($scope.payload);

              const roleIdsList: any[] = _.map($scope.userRoles, (role) => {
                return {roleId: role.id}
              });

              const groupIdsList: any[] = _.map($scope.userGroups, (group) => {
                return {rolesGroupId: group.id}
              })
              payloadCopy.bindings = roleIdsList.concat(groupIdsList);

              $scope._createApiKey(payloadCopy);
            }
          }
        }

        $scope.confirm = () => {
          if ($scope.createdApiKey) {
            self.$mdDialog.hide($scope.createdApiKey);
          }
        }

        $scope.copy = (name) => {
          const copyElement = document.createElement("textarea");
          copyElement.style.position = 'fixed';
          copyElement.style.opacity = '0';
          copyElement.textContent = name;

          const body = document.getElementsByTagName('body')[0];
          body.appendChild(copyElement);
          copyElement.select();
          document.execCommand('copy');
          body.removeChild(copyElement);
        }

        $scope._createApiKey = (apiKeyToAdd) => {
          $scope.currentView = 'loading';
          self.policiesRequestsService.addNewServiceAccount(policyId, apiKeyToAdd, orgId, siteId)
            .then((createdApiKey) => {
              $scope.currentView = 'result';
              $scope.createdApiKey = createdApiKey;
              self.policiesAlertsService.addAPIKeySuccess();
            })
            .catch((error) => {
              self.$log.error('Failed to add API Key @policyView@onAddKey', error);
              self.policiesAlertsService.addAPIKeyError();
            })
        }
      },
      targetEvent: event,
      clickOutsideToClose: false
    })
  }

  // ******************************
  // Internal methods
  // ******************************

  // _getRoleOptionsWithPermissions(roles) {
  //   return _.map(roles, (r) => {
  //     r.permission = r.id === 'billing.manager' ?
  //       'organizations.create' :
  //       (r.id === 'site.reports' ?
  //         'report.generate.list' : 'iam.policy.get');
  //
  //     return r;
  //   })
  // }

}

angular
  .module('bringoz.admin.policies')
  .service('policiesDialogService', PoliciesDialogService);
